import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import { client, accountsClient } from "../../sdkAdapter";
import UserEditModal from "./UserEditModal";

interface User {
  id: number;
  name: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  email: string;
  created_at?: string;
  role: "Admin" | "Customer" | string[];
  status: "Active" | "Inactive";
  active?: boolean;
  image?: string;
  email_verified_at?: string;
  [key: string]: any;
}

export default function UsersTable() {
  const [loading, setLoading] = useState(false);
  const [cmsUsers, setCmsUsers] = useState<User[]>([]);
  const [clientUsers, setClientUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const normalizeUser = (user: any, role: User["role"]): User => ({
    ...user,
    name:
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      user.username ||
      user.email,
    role,
    status: user.active ? "Active" : "Inactive",
  });

  const filterUsers = (users: User[]) => {
    if (!searchTerm) {
      return users;
    }

    const term = searchTerm.toLowerCase();

    return users.filter((user) => {
      const roleLabel = Array.isArray(user.role)
        ? user.role.join(" ")
        : user.role;

      return (
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        roleLabel.toLowerCase().includes(term)
      );
    });
  };

  const dedupeUsers = (users: User[]) =>
    Array.from(new Map<number, User>(users.map((user) => [user.id, user])).values());

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = (await accountsClient.getUsers()) as any;
      const data = res.data.data || res.data;

      const nextCmsUsers = Array.isArray(data.cms_users)
        ? data.cms_users.map((user: any) => normalizeUser(user, "Admin"))
        : [];

      const nextClientUsers = Array.isArray(data.client_users)
        ? data.client_users.map((user: any) => normalizeUser(user, "Customer"))
        : Array.isArray(data)
          ? data.map((user: any) => normalizeUser(user, "Customer"))
          : [];

      setCmsUsers(dedupeUsers(nextCmsUsers));
      setClientUsers(dedupeUsers(nextClientUsers));
    } catch (err: any) {
      if (err.response && err.response.status === 403) {
        setError("Access Denied: You do not have permission to view users (403).");
        return;
      }
      setError(err?.message || String(err));
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = async (user: User) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${user.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await client.postApiAccountsUserDelete({
        postApiAccountsUserDeleteRequest: { id: user.id },
      });
      fetchUsers();
    } catch (err: any) {
      console.error("Failed to delete user:", err);
      Swal.fire({
        title: "Error",
        text: "Failed to delete user: " + (err?.message || "Unknown error"),
        icon: "error",
      });
    }
  };

  const handleModalSave = () => {
    fetchUsers();
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredCmsUsers = filterUsers(cmsUsers);
  const filteredClientUsers = filterUsers(clientUsers);
  const totalUsers = cmsUsers.length + clientUsers.length;
  const totalFilteredUsers =
    filteredCmsUsers.length + filteredClientUsers.length;

  const renderUserTable = (
    title: string,
    description: string,
    users: User[],
    filteredUsers: User[]
  ) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-100 px-4 py-4 dark:border-white/[0.05]">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-white/90">
              {title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          </div>
          <Badge size="sm" color="light">
            {filteredUsers.length} / {users.length}
          </Badge>
        </div>
      </div>

      <div className="max-w-full overflow-x-auto">
        <Table>
          <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
            <TableRow>
              <TableCell
                isHeader
                className="px-5 py-3 text-start font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                User
              </TableCell>
              <TableCell
                isHeader
                className="px-5 py-3 text-start font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                Role
              </TableCell>
              <TableCell
                isHeader
                className="px-5 py-3 text-start font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                Status
              </TableCell>
              <TableCell
                isHeader
                className="px-5 py-3 text-start font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                Verified
              </TableCell>
              <TableCell
                isHeader
                className="px-5 py-3 text-start font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                Joined
              </TableCell>
              <TableCell
                isHeader
                className="px-5 py-3 text-end font-medium text-gray-500 text-theme-xs dark:text-gray-400"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHeader>

          <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
            {filteredUsers.length === 0 && !loading && !error && (
              <TableRow>
                <TableCell
                  className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                  colSpan={6}
                >
                  {searchTerm
                    ? `No ${title.toLowerCase()} found matching "${searchTerm}"`
                    : `No ${title.toLowerCase()} found.`}
                </TableCell>
              </TableRow>
            )}

            {filteredUsers.map((user) => (
              <TableRow key={`${title}-${user.id}`}>
                <TableCell className="px-5 py-4 text-start sm:px-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gray-200">
                      {user.image ? (
                        <img
                          src={
                            user.image.startsWith("http")
                              ? user.image
                              : `${(
                                  import.meta.env.VITE_API_BASE ||
                                  "http://127.0.0.1:8000"
                                ).replace(/\/$/, "")}/${user.image.replace(
                                  /^\//,
                                  ""
                                )}`
                          }
                          alt={user.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-500">
                          {(user.name || "U").charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                        {user.name}
                      </span>
                      <span className="block text-gray-500 text-theme-xs dark:text-gray-400">
                        {user.email}
                      </span>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="px-4 py-3 text-start text-gray-500 text-theme-sm dark:text-gray-400">
                  <Badge
                    size="sm"
                    color={
                      user.role === "Admin" ||
                      (Array.isArray(user.role) && user.role.includes("admin"))
                        ? "success"
                        : "primary"
                    }
                  >
                    {Array.isArray(user.role) ? user.role.join(", ") : user.role}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-start text-gray-500 text-theme-sm dark:text-gray-400">
                  <Badge
                    size="sm"
                    color={user.status === "Active" ? "success" : "error"}
                  >
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-start text-gray-500 text-theme-sm dark:text-gray-400">
                  <Badge
                    size="sm"
                    color={user.email_verified_at ? "success" : "warning"}
                  >
                    {user.email_verified_at ? "Verified" : "Not Verified"}
                  </Badge>
                </TableCell>
                <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                  {formatDate(user.created_at)}
                </TableCell>
                <TableCell className="px-4 py-3 text-end">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-blue-600 hover:underline"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:underline"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
        <div className="border-b border-gray-100 p-4 dark:border-white/[0.05]">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-white/90">
                Users Directory
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                View CMS users and client users separately.
              </p>
            </div>

            <div className="flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
              <div className="relative flex-1 sm:flex-initial">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pl-10 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:w-64"
                />
                <svg
                  className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              <button
                onClick={handleAddUser}
                className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add User
              </button>

              <button
                onClick={fetchUsers}
                disabled={loading}
                className="whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          {searchTerm && (
            <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Showing {totalFilteredUsers} of {totalUsers} users matching "
              {searchTerm}"
            </div>
          )}
        </div>

        {error && (
          <div className="m-4 rounded-lg bg-red-100 p-4 text-sm text-red-700 dark:bg-red-200 dark:text-red-800">
            {error}
          </div>
        )}
      </div>

      {renderUserTable(
        "CMS Users",
        "Administrative users who can access the dashboard.",
        cmsUsers,
        filteredCmsUsers
      )}

      {renderUserTable(
        "Client Users",
        "Customer accounts created on the storefront or client side.",
        clientUsers,
        filteredClientUsers
      )}

      <UserEditModal
        user={editingUser}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleModalSave}
      />
    </div>
  );
}
